import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { OPTIONAL_NOTIFICATION_CHANNELS } from "@titleverify/shared";
import { apiErrorText, apiFetch, type ConsentRevocation } from "../api.js";
import { useAuth } from "../auth.js";
import { CHANNEL_CONSENT } from "../intakeCopy.js";

/**
 * WITHDRAWING CONSENT.
 * ===========================================================================
 *
 * A PDPA obligation rather than a nicety, and until this existed the intake
 * screen's promise — "you can withdraw either of them afterwards" — was
 * followed by an admission that there was nowhere to do it.
 *
 * THIS IS NOT A PREFERENCES PANEL, AND IT MUST NOT LOOK LIKE ONE. The reason is
 * blunt: NO ENDPOINT RETURNS CURRENT CONSENT STATE. `hasConsentForCase` exists
 * in the API but is exported only for its tests, and no route calls it. So the
 * browser has no way to know whether email or WhatsApp is on for a case.
 *
 * A checkbox or a toggle would state that anyway. An unticked box IS a claim
 * that the channel is off, and a ticked one that it is on — the control's
 * position is the assertion, whatever the label says. Rendering either from
 * knowledge we do not have is a fabricated fact about our own records, which is
 * non-negotiable 1 aimed at ourselves rather than at a registry. So what is
 * drawn is a one-way ACT: "withdraw this", which asserts nothing about the
 * current state, and the `{ revoked: n }` count that comes back is the only
 * consent signal this app ever gets. It arrives after the act, never before.
 *
 * THE ASYMMETRY WITH THE SERVER, STATED HONESTLY. The endpoint accepts the full
 * CONSENT_PURPOSES x CONSENT_CHANNELS product. This offers exactly one purpose
 * (`delivery_notification`) over OPTIONAL_NOTIFICATION_CHANNELS, so `in_app`,
 * `case_processing` and `document_retention` are not offered here:
 *
 *  - `in_app` for delivery_notification is where the report is DELIVERED, not a
 *    notification preference. A case with no way to reach the client is not a
 *    case.
 *  - `case_processing` and `document_retention` are the lawful basis for work
 *    the client asked for and for documents already under a retention clock.
 *    Withdrawing either through this endpoint would stamp `revoked_at` with
 *    NOTHING in the codebase reacting to it — producing a record that says the
 *    basis was withdrawn while the case carries on regardless. That is a
 *    question for the reviewing attorney, not a button to ship.
 *
 * The important part: on those three the server is NOT the backstop. It would
 * accept them. So this omission is a product decision made here, and the copy
 * never tells the client that the platform prevents it — claiming a refusal
 * that does not exist would be its own small fabrication.
 */

type Scope = { caseId: string } | { allCases: true };
type Channel = (typeof OPTIONAL_NOTIFICATION_CHANNELS)[number];

export function ConsentWithdrawal({ scope }: { scope: Scope }) {
  const { getToken } = useAuth();
  const accountWide = "allCases" in scope;

  /*
   * Results and errors are held PER CHANNEL rather than read off the mutation.
   * TanStack keeps one status for the hook, so withdrawing email and then
   * WhatsApp would move email's answer onto WhatsApp's row — reporting a count
   * against a channel it was never about.
   */
  const [counts, setCounts] = useState<Partial<Record<Channel, number>>>({});
  const [errors, setErrors] = useState<Partial<Record<Channel, unknown>>>({});

  const withdraw = useMutation({
    mutationFn: async (channel: Channel) =>
      apiFetch<ConsentRevocation>("/consents/revoke", {
        token: await getToken(),
        method: "POST",
        body: { purpose: "delivery_notification", channel, scope },
      }),
    onSuccess: (data, channel) => {
      setCounts((current) => ({ ...current, [channel]: data.revoked }));
      setErrors((current) => ({ ...current, [channel]: undefined }));
    },
    onError: (error, channel) => {
      setErrors((current) => ({ ...current, [channel]: error }));
    },
  });

  /*
   * NOTHING IS INVALIDATED ON SUCCESS, and that is not an oversight. There is no
   * query anywhere in this app whose answer a withdrawal changes, because there
   * is no consent query at all. Invalidating the case list to look busy would
   * refetch data that says nothing about consent.
   */

  return (
    <div className="stack">
      <div className="consequence">
        <h3>{accountWide ? "Withdrawing across every request" : "Withdrawing for this request"}</h3>

        {accountWide ? (
          <p>
            This covers <strong>every request you have with us, present and past</strong> —
            not just one. If you only want to stop a channel on a single request, do it on
            that request instead.
          </p>
        ) : (
          <p>
            This covers <strong>this request only</strong>. Consent is recorded per request,
            so withdrawing here leaves your other requests exactly as they are.
          </p>
        )}

        <p>
          <strong>It cannot be undone.</strong> A withdrawal is permanent: there is no way
          through this app to grant a channel again for a request that already exists.
          {accountWide
            ? " If you open a new request later, that new request will ask you about these channels again, and your answer there will apply to it."
            : " If you wanted it back you would have to open a new request."}
        </p>

        <p>
          <strong>Nothing is deleted.</strong> The record that you gave permission earlier is
          kept and marked as withdrawn from today. That record is the evidence that what we
          did before was done with your agreement — erasing it would remove the proof, not
          the processing.
        </p>

        {/*
          WHAT THIS ACT ACTUALLY IS, said plainly. No code path in this platform
          sends email or WhatsApp today — nothing calls the consent check — so
          "your messages will stop" would be claiming a cause we have not
          established. The honest description is what gets RECORDED. Overstating
          here is the same failure as stating a title fact we have not verified.
        */}
        <p>
          <small>
            To be exact about what this does: it records your withdrawal against the
            permission we hold, and that record is what a send is checked against. Withdrawing
            email stops the one message this platform sends — the note that an attorney's
            opinion is ready. WhatsApp has nothing to stop yet: no part of the platform can
            send on it, and there is nowhere here to give us a number.
          </small>
        </p>
      </div>

      {/*
        SAID BEFORE THE BUTTONS, NOT AFTER. The client is about to press
        something and the reasonable assumption is that a screen offering to
        withdraw a channel knows whether that channel is on. It does not.
      */}
      <div className="caveat">
        <p>
          <strong>This screen cannot show you which channels are currently on.</strong> We
          have not built the part that reads back what you have agreed to, so both are listed
          whether or not you ever chose them. Withdrawing one you never chose does no harm and
          changes nothing — it will simply tell you there was nothing on record.
        </p>
      </div>

      {OPTIONAL_NOTIFICATION_CHANNELS.map((channel) => {
        const count = counts[channel];
        const error = errors[channel];
        const pending = withdraw.isPending && withdraw.variables === channel;
        /*
         * A per-case withdrawal is spent once it has answered, whatever the
         * count. Consent rows are inserted ONLY at case submission, so for a
         * case that already exists no new live row can ever appear and pressing
         * again could not do anything different. Account-wide is not spent: a
         * request opened later records fresh consent, so the control stays.
         */
        const spent = count !== undefined && !accountWide;

        return (
          <div key={channel} className="stack">
            <h4>{CHANNEL_CONSENT[channel].label}</h4>
            <p>
              <small>{CHANNEL_CONSENT[channel].meaning}</small>
            </p>

            {count === undefined ? null : count > 0 ? (
              /*
               * .decided — a decision already taken, visually settled. Not an
               * .outcome the client is meant to act on and emphatically not an
               * alert: they asked for this and it happened.
               */
              <div className="decided">
                <p>
                  <strong>Withdrawn.</strong> {CHANNEL_CONSENT[channel].withdrawn}
                </p>
                <p>
                  <small>
                    {count === 1
                      ? "One permission on record was live and has been marked withdrawn"
                      : `${count} permissions on record were live and have been marked withdrawn`}
                    {accountWide ? " across your requests." : " for this request."}
                  </small>
                </p>
              </div>
            ) : (
              /*
               * ZERO. Neither a success nor a failure, and the trap on this
               * endpoint. It means there was nothing live to withdraw — never
               * granted, or already withdrawn. ".notice": true, worth knowing,
               * not a problem. "Withdrawn." over a no-op would report an act
               * that did not happen; role="alert" would report a fault that did
               * not occur.
               *
               * It also does not mean "that request is not yours". The endpoint
               * is scoped to the caller's own rows and refuses nothing, so a
               * request belonging to somebody else matches nothing and returns
               * exactly this. No copy here may read a zero as an answer about
               * ownership.
               */
              <div className="notice">
                <p>
                  <strong>Nothing was withdrawn, because there was nothing to withdraw.</strong>{" "}
                  We hold no live permission for this channel
                  {accountWide ? " on any of your requests" : " on this request"} — either it
                  was never given, or it was withdrawn before. Nothing changed.
                </p>
              </div>
            )}

            {error ? (
              <p role="alert">
                {apiErrorText(error, "Could not record your withdrawal, so nothing changed.")}
              </p>
            ) : null}

            {spent ? null : (
              <div className="actions">
                <button
                  type="button"
                  className="danger"
                  disabled={withdraw.isPending}
                  onClick={() => withdraw.mutate(channel)}
                >
                  {pending
                    ? "Recording…"
                    : accountWide
                      ? `Withdraw ${LABEL[channel]} for every request`
                      : `Withdraw ${LABEL[channel]} for this request`}
                </button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/**
 * The channel as it appears mid-sentence on a button. Not a second copy of
 * CHANNEL_CONSENT — that table holds the disclosures, and shortening one of
 * those to fit a button is how a cross-border transfer notice gets lost.
 */
const LABEL: Record<Channel, string> = {
  email: "email",
  whatsapp: "WhatsApp",
};
